import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "../lib/usePageMeta";
import { House, FileText, ShieldCheck, EnvelopeSimple, WarningCircle } from "@phosphor-icons/react";

/**
 * Standalone research-eligibility-declined explanation page.
 *
 * Reached only when the shopper explicitly clicks "No" on
 * /research-eligibility. There is NO automatic redirect from that page — the
 * shopper's click brings them here.
 *
 * This page intentionally does NOT link to /shop, product pages, or any part
 * of the catalog. It links only to homepage, research-use policy, privacy
 * policy, and contact.
 */

export default function ResearchEligibilityDeclined() {
  usePageMeta({
    title: "Research Access Not Applicable",
    description:
      "Access to the Medical Peptide Store catalog is available only for qualified laboratory research use. Products are not for human or veterinary use.",
    canonicalPath: "/research-eligibility/declined",
    ogType: "website",
    robots: "noindex, follow",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Research Access Not Applicable — Medical Peptide Store",
        "url": "https://medicalpeptidestore.com/research-eligibility/declined",
        "description":
          "Medical Peptide Store catalog is a research-use-only reference-standard supplier. This page explains why access is unavailable for non-research use.",
        "inLanguage": "en-US",
        "isPartOf": { "@id": "https://medicalpeptidestore.com/#website" },
        "about": { "@id": "https://medicalpeptidestore.com/#organization" },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://medicalpeptidestore.com/",
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Research Eligibility",
            "item": "https://medicalpeptidestore.com/research-eligibility",
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Not Applicable",
            "item": "https://medicalpeptidestore.com/research-eligibility/declined",
          },
        ],
      },
    ],
  });

  useEffect(() => {
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "eligibility_no_view", {
          event_category: "eligibility",
          event_label: "research_eligibility_declined",
          page_path: "/research-eligibility/declined",
        });
      }
      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "eligibility_no_view",
          gate: "research_eligibility_declined",
        });
      }
    } catch (err) {
      // Analytics is best-effort; log so failures remain traceable.
      // eslint-disable-next-line no-console
      console.warn("eligibility_no_view analytics dispatch failed:", err);
    }
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-16" data-testid="research-eligibility-declined-page">
      <div className="overline mb-3">Access Notice</div>
      <h1
        className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-brand-navy"
        data-testid="declined-headline"
      >
        Access to the catalog is not available.
      </h1>

      <div
        className="mt-8 border border-brand-border bg-brand-surface p-8 md:p-10"
        data-testid="declined-explanation"
      >
        <div className="flex items-start gap-4">
          <WarningCircle size={28} weight="duotone" className="text-brand-accent shrink-0" aria-hidden />
          <div className="text-sm md:text-[15px] text-brand-navy leading-relaxed space-y-3">
            <p>
              <strong>Medical Peptide Store</strong> sells peptide{" "}
              <em>reference standards</em> intended for qualified laboratory
              research use only.
            </p>
            <p className="text-brand-muted">
              Because our catalog is intended only for qualified laboratory research,
              access is not available if you are under 21 or purchasing for any purpose
              other than qualified laboratory research. Our products are not for human
              consumption, not for veterinary use, and are not intended to diagnose,
              treat, cure, or prevent any disease.
            </p>
            <p className="text-brand-muted">
              If you were referred here in error, please contact us. Otherwise,
              we thank you for your interest.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-12 border-t border-brand-border pt-10">
        <div className="overline mb-3">Where to go from here</div>
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          data-testid="declined-links"
        >
          <Link
            to="/"
            className="border border-brand-border bg-white p-6 hover:border-brand-navy transition-colors group"
            data-testid="declined-link-home"
          >
            <House size={22} weight="duotone" className="text-brand-accent" aria-hidden />
            <div className="mt-3 font-display text-lg text-brand-navy font-semibold">Return to homepage</div>
            <div className="mt-1 text-xs text-brand-muted">Learn about Medical Peptide Store.</div>
          </Link>

          <Link
            to="/research-use-policy"
            className="border border-brand-border bg-white p-6 hover:border-brand-navy transition-colors group"
            data-testid="declined-link-research-use-policy"
          >
            <FileText size={22} weight="duotone" className="text-brand-accent" aria-hidden />
            <div className="mt-3 font-display text-lg text-brand-navy font-semibold">Research-Use Policy</div>
            <div className="mt-1 text-xs text-brand-muted">Who our products are for and how they may be used.</div>
          </Link>

          <Link
            to="/privacy"
            className="border border-brand-border bg-white p-6 hover:border-brand-navy transition-colors group"
            data-testid="declined-link-privacy"
          >
            <ShieldCheck size={22} weight="duotone" className="text-brand-accent" aria-hidden />
            <div className="mt-3 font-display text-lg text-brand-navy font-semibold">Privacy Policy</div>
            <div className="mt-1 text-xs text-brand-muted">What information we collect and how it is handled.</div>
          </Link>

          <Link
            to="/contact"
            className="border border-brand-border bg-white p-6 hover:border-brand-navy transition-colors group"
            data-testid="declined-link-contact"
          >
            <EnvelopeSimple size={22} weight="duotone" className="text-brand-accent" aria-hidden />
            <div className="mt-3 font-display text-lg text-brand-navy font-semibold">Contact Us</div>
            <div className="mt-1 text-xs text-brand-muted">Reach the compliance and support team.</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
